import './VehicleSearch.scss'
import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, ToggleButton, ToggleButtonGroup, Modal } from 'react-bootstrap';
import { UserAuthContext, Authorize } from "../../appLoginComponent/UserAuthContext";
import NotAuthorized from '../../appLoginComponent/NotAuthorized';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from '../../../../services/appInsights.service';
import { ResponderService } from '../../../../services/responder.service';
import Spinner from '../../../components/Spinner';
import { VehicleInfo } from '../../../../interfaces/responder.interface';

function VehicleSearch() {
    let { isAuthorized } = useContext(
        UserAuthContext
    ) as Authorize;
    const [searchVehicleType, setSearchVehicleType] = useState('plate');
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [multipleVehicles, setMultipleVehicles] = useState<VehicleInfo[]>([]);
    const [showVehicleSelection, setShowVehicleSelection] = useState(false);
    const navigate = useNavigate();

    const licensePlate = sessionStorage?.getItem('licensePlateNumber') ?? '';
    const uniqueNumber = sessionStorage?.getItem('uniqueNumber') ?? '';

    useEffect(() => {
        if (licensePlate) {
            setSearchTerm(licensePlate);  // "ABR-8978"
        }
        else if (uniqueNumber) {
            setSearchTerm(uniqueNumber);  // "12345"
        }
        sessionStorage.removeItem('licensePlate');
        sessionStorage.removeItem('uniqueNumber');
    }, [licensePlate, uniqueNumber]);

    useEffect(() => {
        sessionStorage.getItem('searchPdd') && setSearchVehicleType('pdd');
        sessionStorage.getItem('searchPlate') && setSearchVehicleType('plate');
        sessionStorage.removeItem('searchPdd')
        sessionStorage.removeItem('searchPlate')
    }, [])

    async function handleSearch() {
        setIsLoading(true);
        setShowVehicleSelection(false);
        setMultipleVehicles([]);

        if (searchTerm === "") {
            setErrorMessage(`Please enter a valid ${searchVehicleType === "plate" ? "License Plate" : "Unique ID"}`);
            setShowError(true);
        }
        else {
            const responderService = new ResponderService();
            await responderService.GetVehicleByLicensePlateOrUniqueId(searchVehicleType, searchTerm).then((response) => {
                if (response.result.length == 0) {
                    setErrorMessage(`No matching vehicles found for that ${searchVehicleType === "plate" ? "License Plate" : "Unique ID"}`);
                    setShowError(true);
                }
                else if (response.result.length === 1) {
                    // Single result - navigate directly
                    sessionStorage.setItem('searchVehicleType', searchVehicleType);
                    navigate(`/mobileView`, { state: { vehicle: response.result[0], searchVehicleType: searchVehicleType } });
                }
                else if (response.result.length > 1) {
                    // Multiple results - show selection UI
                    setMultipleVehicles(response.result);
                    setShowVehicleSelection(true);
                    setShowError(false);
                }

            }).catch((error) => {
                // Use statusMessage from backend response
                const backendMessage = error?.response?.data?.statusMessage;
                setErrorMessage(backendMessage || `An error occurred while searching for the ${searchVehicleType === "plate" ? "License Plate" : "Unique ID"}. Please try again later.`);
                setShowError(true);
            });
        }
        setIsLoading(false);
    };

    function handleVehicleSelection(vehicle: VehicleInfo) {
        sessionStorage.setItem('searchVehicleType', searchVehicleType);
        navigate(`/mobileView`, { state: { vehicle: vehicle, searchVehicleType: searchVehicleType } });
    }

    function handleBackToSearch() {
        setShowVehicleSelection(false);
        setMultipleVehicles([]);
    }

    return (
        <>
            {isAuthorized("AV_Vehicle_Search") ? (
                <div className='vehicle-search-container'>
                    <div className="vehicle-info-title">
                        <h5>Search for {`${searchVehicleType === "plate" ? "Vehicle" : "PDD"}`}</h5>
                    </div>
                    <ToggleButtonGroup
                        className="vehicle-type w-100 mt-3"
                        type="radio"
                        value={searchVehicleType}
                        name="vehicleTypeId">
                        <ToggleButton id="search-vehicle-plate" className="vehicle-type-button" onChange={e => { setSearchVehicleType(e.target.value); licensePlate !== '' && setSearchTerm(licensePlate || '') }} variant="light" value={"plate"}>VEHICLE</ToggleButton>
                        <ToggleButton id="search-vehicle-pdd" className="vehicle-type-button" onChange={e => {
                            setSearchVehicleType(e.target.value);
                            uniqueNumber !== '' && setSearchTerm(uniqueNumber)
                        }} variant="light" value={"pdd"}>PDD</ToggleButton>
                    </ToggleButtonGroup>
                    <div className='mt-4 text-danger'>
                        {showError ? (
                            <p className='text-danger p-0'>{errorMessage}</p>
                        ) : (
                            <></>
                        )}
                        <Form.Control maxLength={25} name="search" placeholder={searchVehicleType === "plate" ? "Enter License Plate" : "Enter Unique Id"} onFocus={() => setShowError(false)} onChange={(e) => setSearchTerm(e.target.value)} value={searchTerm} size="lg" type="text" />
                    </div>
                    <div className='mt-2'>
                        <Button
                            disabled={isLoading}
                            onClick={isLoading ? () => { } : handleSearch}
                            className="search-button w-100"
                        >
                            SEARCH
                        </Button>
                    </div>
                    {isLoading ? <Spinner /> : <></>}

                    {/* Vehicle Selection Modal */}
                    <Modal
                        show={showVehicleSelection}
                        onHide={handleBackToSearch}
                        centered
                        className="vehicle-selection-modal"
                    >
                        <Modal.Body className="p-4">
                            <div className="vehicle-selection-list">
                                {multipleVehicles.map((vehicleData) => {
                                    // Use a unique key, fallback to JSON string if necessary
                                    const key = `${vehicleData.vehicleRegistrationStateCd || ''}-${vehicleData.licensePlateNumber || ''}-${vehicleData.uniqueNumber || ''}`;
                                    return (
                                        <button
                                            key={key}
                                            className="vehicle-option btn btn-link p-0 text-start w-100"
                                            type="button"
                                            onClick={() => handleVehicleSelection(vehicleData as unknown as VehicleInfo)}
                                        >
                                            <div className="vehicle-state-plate">
                                                {vehicleData.vehicleRegistrationStateCd} - {vehicleData.licensePlateNumber}
                                            </div>
                                            <div className="vehicle-model">
                                                {vehicleData.vehicleMake} {vehicleData.vehicleModel} {vehicleData.year}
                                            </div>
                                        </button>
                                    )
                                })}
                            </div>
                            <Button
                                onClick={handleBackToSearch}
                                className="cancel-button w-100 mt-3"
                            >
                                Cancel
                            </Button>
                        </Modal.Body>
                    </Modal>
                </div>
            ) : (
                <NotAuthorized />
            )}
        </>
    )
}

export default withAITracking(reactPlugin, VehicleSearch);